import styled from '@emotion/styled';

import { isEmpty } from 'lodash';

import { useCallback, useMemo } from 'react';

import { useAppSelector } from '../../../../../../shared/store';
import {
  selectCurrentTotalPoint,
  selectCurrentTotalPrice,
  selectRestockNotifiableDealProducts,
} from '../../../../selectors';

import useProductBuy from '../../../../hooks/useProductBuy';
import usePickProduct from '../../../../hooks/usePickProduct';

import OptionSection from '../option-section/OptionSection';
import AdultInformation from '../../description/AdultInformation';
import PurchasePriceResult from './PurchasePriceResult';
import PurchaseButtonGroup from './PurchaseButtonGroup';
import Alert from '../../../../../../shared/components/Alert/Alert';
import { amplitudeService } from '../../../../../../shared/amplitude';
import {
  SelectPurchase,
} from '../../../../../../shared/amplitude/events/product/SelectPurchase';
import { getFusionQueryId } from '../../../../shared/utils/productDetailEvent';

const Container = styled.div`
  padding-bottom: 40px;
`;

const PurchasePriceResultWrapper = styled.div`
  padding-top: 30px;
`;

interface Props {
  loggedIn: boolean;
}

export default function BuySection({ loggedIn }: Props) {
  const { queryId } = useAppSelector(({ productList }) => productList);
  const productDetailState = useAppSelector(({ productDetail }) => productDetail);

  const {
    no,
    defaultContentId,
    dealProducts,
    discountedPrice,
    contentType,
    groupProduct,
    isDirectOrder,
    isSoldOut,
    isPurchaseStatus,
    canRestockNotify,
    isOnlyAdult,
    isExpectedPoint,
  } = productDetailState;

  const restockNotifiableDealProducts = useAppSelector(selectRestockNotifiableDealProducts);
  const { buyText, buyProduct } = useProductBuy({
    isDirectOrder, isSoldOut, isPurchaseStatus,
  });

  const { isActiveLike, toggleLike } = usePickProduct(no);

  const totalPrice = useAppSelector(selectCurrentTotalPrice);
  const totalPoint = useAppSelector(selectCurrentTotalPoint);

  const resultTotalPoint = isExpectedPoint ? totalPoint : 0;

  const isRestockNotificationAvailable = useMemo(() =>
    canRestockNotify && !isEmpty(restockNotifiableDealProducts),
  [canRestockNotify, restockNotifiableDealProducts]);

  const isDiscountProduct = useMemo(() => !!discountedPrice, [discountedPrice]);

  const onClickCartButton = useCallback(async () => {
    await amplitudeService
      .logEvent(new SelectPurchase({
        productDetailState,
        defaultContentId,
        fusionQueryId: getFusionQueryId(queryId),
      }));

    try {
      await buyProduct();
    } catch (error) {
      Alert({
        title: '알림',
        text: error.message,
      });
    }
  }, [buyProduct, defaultContentId, productDetailState, queryId]);

  return (
    <Container>
      <OptionSection
        productNo={no}
        isSoldOut={isSoldOut}
        isPurchaseStatus={isPurchaseStatus}
        dealProducts={dealProducts}
        groupProduct={groupProduct}
        contentType={contentType}
      />
      {isOnlyAdult && <AdultInformation />}
      <PurchasePriceResultWrapper>
        <PurchasePriceResult
          loggedIn={loggedIn}
          totalPrice={totalPrice}
          totalPoint={resultTotalPoint}
          isDiscountProduct={isDiscountProduct}
        />
      </PurchasePriceResultWrapper>
      <PurchaseButtonGroup
        isActiveLike={isActiveLike}
        isRestockNotificationAvailable={isRestockNotificationAvailable}
        buyText={buyText}
        disabled={isSoldOut || !isPurchaseStatus}
        toggleLike={toggleLike}
        onClickCartButton={onClickCartButton}
      />
    </Container>
  );
}
